<template>
    <div class="max-w-6xl mx-auto p-4 flex flex-col gap-6">
        <div class="flex flex-col gap-1">
            <router-link to="/app/plan" class="text-sm text-theme-muted">
                ← Volver a Mi plan
            </router-link>
            <h1 class="text-2xl font-semibold text-theme-base">
                Cambiar de plan
            </h1>
            <p v-if="trialEndsAt" class="text-sm text-theme-muted">
                Estás probando {{ currentPlanName }} gratis hasta el
                {{ trialEndsAt | dateSimple }}. El plan que elijas empieza ese
                día y lo puedes pagar desde hoy, sin perder días de prueba. Si
                subes a un plan mayor, empieza hoy. También puedes quedarte con
                {{ currentPlanName }} y elegir cada cuánto pagarlo: el periodo
                que elijas empieza cuando termine la prueba.
            </p>
            <p v-else class="text-sm text-theme-muted">
                Elige el plan y cada cuánto quieres pagarlo. Si subes de plan o
                pasas a un periodo más largo, el cambio es inmediato y
                descontamos lo que no alcanzaste a usar. Si bajas de plan o
                pasas a un periodo más corto, el cambio aplica en tu fecha de
                corte.
            </p>
        </div>

        <p v-if="loaded && !plans.length" class="text-sm text-theme-muted">
            No hay otros planes disponibles.
        </p>

        <div v-if="periods.length > 1" class="flex justify-center">
            <plan-period-selector v-model="selectedPeriod" :periods="periods" />
        </div>

        <div class="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
            <div
                v-for="group in groups"
                :key="group.code"
                class="relative rounded-2xl bg-theme-card p-5 flex flex-col gap-3"
                :class="
                    isCurrentGroup(group)
                        ? 'border-2 border-theme-primary'
                        : 'border border-theme-border'
                "
            >
                <span
                    v-if="isCurrentGroup(group)"
                    class="absolute -top-3 right-4 bg-theme-primary text-theme-inverse text-xs font-semibold px-3 py-1 rounded-full"
                >
                    {{ currentBadge }}
                </span>
                <h2 class="text-xl font-bold text-theme-base">
                    {{ group.name }}
                </h2>
                <p v-if="group.description" class="text-sm text-theme-muted">
                    {{ group.description }}
                </p>
                <div>
                    <p class="whitespace-nowrap">
                        <span class="text-2xl font-bold text-theme-base">
                            {{ rowOf(group).price | currency }}
                        </span>
                        <span class="text-sm text-theme-muted">
                            {{ periodSuffix(rowOf(group)) }}</span
                        >
                    </p>
                    <p
                        v-if="rowOf(group).period !== 'monthly'"
                        class="text-sm text-theme-muted mt-1"
                    >
                        Equivale a
                        {{ monthlyEquivalent(rowOf(group)) | currency }} al mes
                    </p>
                    <p
                        v-if="savings(group) > 0"
                        class="text-sm font-bold text-theme-success"
                    >
                        Ahorras {{ savingsAmount(group) | currency }} ({{
                            savings(group)
                        }}
                        %)
                    </p>
                </div>
                <p
                    v-if="showsCurrentPrice(group)"
                    class="text-xs text-theme-muted"
                >
                    Hoy pagas {{ currentRow.price | currency }}
                    {{ periodSuffix(currentRow) }}
                </p>
                <div class="mt-auto">
                    <app-button
                        color="brand-blue"
                        :loading="previewingSlug === rowOf(group).slug"
                        :disabled="
                            Boolean(previewingSlug) || isCurrentRow(group)
                        "
                        @click="choosePlan(rowOf(group))"
                    >
                        {{ buttonLabel(group) }}
                    </app-button>
                </div>
            </div>
        </div>

        <div v-if="allPlans.length > 1" class="flex flex-col gap-3">
            <h2 class="text-lg font-semibold text-theme-base">
                Compara los planes
            </h2>
            <plan-comparison-table
                :plans="comparisonPlans"
                :current-plan-slug="currentSlug"
                :monthly-plans="monthlyPlansByCode"
                show-price
            />
        </div>

        <plan-change-confirm-dialog
            v-model="showConfirmDialog"
            :preview="preview"
            :plan-name="selectedPlan ? selectedPlan.name : ''"
            :is-free="isSelectedPlanFree"
            :losses="selectedPlanLosses"
            :loading="isChanging"
            @confirm="confirmChange"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { listPlanLosses } from "@/common/fields/plan-features";
import {
    buildPaymentOpeningRoute,
    savePaymentLink
} from "@/common/lib/payment-link";
import {
    PLAN_PERIOD_LABELS,
    PLAN_PERIOD_SUFFIXES,
    availablePeriods,
    groupPlansByCode,
    isPlanPeriod,
    monthlyEquivalent,
    rowForPeriod,
    savingsAmount,
    savingsPercent
} from "@/common/lib/plan-periods";

import PlanComparisonTable from "@/components/utils/PlanComparisonTable.vue";
import PlanPeriodSelector from "@/components/utils/PlanPeriodSelector.vue";

import PlanChangeConfirmDialog from "./PlanChangeConfirmDialog.vue";

const FREE_PLAN_CODE = "free";

export default {
    name: "PlanChange",
    components: {
        PlanChangeConfirmDialog,
        PlanComparisonTable,
        PlanPeriodSelector
    },
    data: () => ({
        loaded: false,
        plans: [],
        allPlans: [],
        selectedPeriod: "monthly",
        currentSlug: null,
        currentRow: null,
        currentPlanName: "",
        trialEndsAt: null,
        usage: [],
        selectedPlan: null,
        preview: null,
        previewingSlug: null,
        showConfirmDialog: false,
        isChanging: false
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        groups() {
            return groupPlansByCode(this.plans);
        },
        periods() {
            return availablePeriods(
                this.groups.filter((group) => group.code !== FREE_PLAN_CODE)
            );
        },
        currentBadge() {
            if (!this.currentRow || this.currentRow.code === FREE_PLAN_CODE) {
                return "Tu plan";
            }
            return `Tu plan · ${PLAN_PERIOD_LABELS[this.currentRow.period]}`;
        },
        // Una columna por plan: la del plan vigente con su propio periodo
        // (así siempre aparece), las demás con el periodo elegido.
        monthlyPlansByCode() {
            return this.groups.reduce((acc, group) => {
                if (group.rows.monthly) {
                    acc[group.code] = group.rows.monthly;
                }
                return acc;
            }, {});
        },
        comparisonPlans() {
            return this.groups.map((group) =>
                this.isCurrentGroup(group) && this.currentRow
                    ? this.currentRow
                    : this.rowOf(group)
            );
        },
        isTrial() {
            return Boolean(this.trialEndsAt);
        },
        isSelectedPlanFree() {
            return Boolean(
                this.selectedPlan && this.selectedPlan.code === FREE_PLAN_CODE
            );
        },
        // Lo que Free no incluye, para mostrarlo antes de cancelar.
        selectedPlanLosses() {
            const currentPlan = this.allPlans.find(
                (plan) => plan.slug === this.currentSlug
            );
            if (!this.isSelectedPlanFree || !currentPlan) return null;
            return listPlanLosses({
                fromPlan: currentPlan,
                toPlan: this.selectedPlan,
                usage: this.usage
            });
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (
            !(await verifyPagePermission(
                "platformSubscription:ChangePlanMine",
                "/app/plan"
            ))
        )
            return;
        await this.loadPlans();
    },
    methods: {
        ...mapActions("platformSubscriptions", [
            "getMySubscription",
            "listActivePlatformPlans",
            "previewMyPlanChange",
            "changeMyPlan"
        ]),
        async showError(error) {
            this.$sound.play("error");
            await this.$swal.fire({
                background: this.backgroundColor,
                title: error.title,
                text: error.message,
                icon: error.icon
            });
        },
        async loadPlans() {
            this.$loading.show();
            try {
                const [state, plans] = await Promise.all([
                    this.getMySubscription(),
                    this.listActivePlatformPlans()
                ]);
                const { current } = state;
                const currentSlug = current ? current.planSlug : null;
                const isTrial = Boolean(current && current.type === "trial");
                this.currentSlug = currentSlug;
                this.currentPlanName = current ? current.planName : "";
                this.trialEndsAt = isTrial ? current.currentPeriodEnd : null;
                this.usage = state.usage || [];
                this.allPlans = plans.items;
                // En el trial Free sobra: es a donde pasa solo al terminar
                // la prueba.
                this.plans = plans.items.filter(
                    (plan) => !isTrial || plan.code !== FREE_PLAN_CODE
                );
                const currentRow = plans.items.find(
                    (plan) => plan.slug === currentSlug
                );
                this.currentRow = currentRow || null;
                if (currentRow) this.selectedPeriod = currentRow.period;
                this.loaded = true;
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                await this.showError(error);
                return;
            }
            await this.choosePlanFromQuery();
        },
        rowOf(group) {
            return rowForPeriod(group, this.selectedPeriod);
        },
        periodSuffix(row) {
            return PLAN_PERIOD_SUFFIXES[row.period];
        },
        monthlyEquivalent(row) {
            return monthlyEquivalent(row);
        },
        savings(group) {
            return savingsPercent(this.rowOf(group), group.rows.monthly);
        },
        savingsAmount(group) {
            return savingsAmount(this.rowOf(group), group.rows.monthly);
        },
        isCurrentGroup(group) {
            return Boolean(
                this.currentRow && this.currentRow.code === group.code
            );
        },
        showsCurrentPrice(group) {
            return (
                this.isCurrentGroup(group) &&
                this.rowOf(group).slug !== this.currentSlug
            );
        },
        isCurrentRow(group) {
            return !this.isTrial && this.rowOf(group).slug === this.currentSlug;
        },
        buttonLabel(group) {
            const row = this.rowOf(group);
            if (this.isCurrentRow(group)) return "Tu plan actual";
            return row.slug === this.currentSlug
                ? `Seguir con ${group.name}`
                : `Elegir ${group.name}`;
        },
        // `?plan=<code>` (notificación del trial, modal "Pásate a…") abre
        // directo la confirmación de ese plan, en `?period=` si viene.
        async choosePlanFromQuery() {
            const { period, plan: code } = this.$route.query;
            if (!code) return;
            const group = this.groups.find((item) => item.code === code);
            if (!group) return;
            if (isPlanPeriod(period) && group.rows[period]) {
                this.selectedPeriod = period;
            }
            const row = this.rowOf(group);
            if (this.isCurrentRow(group)) return;
            await this.choosePlan(row);
        },
        async choosePlan(plan) {
            this.previewingSlug = plan.slug;
            try {
                this.preview = await this.previewMyPlanChange({
                    planSlug: plan.slug
                });
                this.selectedPlan = plan;
                this.previewingSlug = null;
                this.showConfirmDialog = true;
            } catch (error) {
                this.previewingSlug = null;
                await this.showError(error);
            }
        },
        async confirmChange() {
            this.isChanging = true;
            try {
                const result = await this.changeMyPlan({
                    planSlug: this.selectedPlan.slug
                });
                if (result.paymentUrl) {
                    savePaymentLink(result.invoiceId, result.paymentUrl);
                    this.$router.push(
                        buildPaymentOpeningRoute("my_plan", result.invoiceId)
                    );
                    return;
                }
                this.isChanging = false;
                this.showConfirmDialog = false;
                this.$toast.success("Cambio de plan programado");
                this.$router.push("/app/plan");
            } catch (error) {
                this.isChanging = false;
                await this.showError(error);
            }
        }
    }
};
</script>
